import type { Affiliation } from './semicolon-csv'
import { isAffiliationTooLong } from '~/helpers/users/affiliation'
import { affiliationOf, cellOf, readSemicolonCsv } from './semicolon-csv'

/**
 * Colonnes du CSV d'import d'apprenants (séparateur `;`, comme l'export).
 * `groupe`, `societe` et `service` sont facultatives : une cellule vide laisse le
 * champ vide, et un fichier sans la colonne reste accepté tel quel (#135, #277).
 */
export const USERS_CSV_HEADERS = ['email', 'firstName', 'lastName', 'groupe', 'societe', 'service'] as const

/** Colonnes sans lesquelles le fichier est refusé. */
const REQUIRED_HEADERS = ['email', 'firstname', 'lastname'] as const

export type ParsedUserRow = Affiliation & {
  email: string
  firstName: string
  lastName: string
  group?: string
}

export type ParseResult
  = | { ok: true, rows: ParsedUserRow[] }
    | { ok: false, error: 'empty' | 'badHeader' }

/**
 * Parse un CSV d'apprenants à en-têtes fixes `email;firstName;lastName` (plus
 * `groupe`, facultative). Tolérant : BOM ignoré, ordre des colonnes libre
 * (résolu par nom), lignes vides filtrées, valeurs trimmées. Ne structure que le
 * fichier : la validation ligne à ligne est le travail de `validateUserRows`.
 */
export function parseUsersCsv(text: string): ParseResult {
  const csv = readSemicolonCsv(text)
  if (!csv) return { ok: false, error: 'empty' }
  if (REQUIRED_HEADERS.some(name => !csv.header.includes(name))) {
    return { ok: false, error: 'badHeader' }
  }

  const rows = csv.rows.map((cells) => {
    const group = cellOf(csv, cells, 'groupe')
    return {
      email: cellOf(csv, cells, 'email'),
      firstName: cellOf(csv, cells, 'firstname'),
      lastName: cellOf(csv, cells, 'lastname'),
      // Champ omis plutôt que chaîne vide : le DTO le veut optionnel, et une
      // cellule vide ne doit pas créer un groupe au libellé blanc.
      ...(group ? { group } : {}),
      ...affiliationOf(csv, cells),
    }
  })

  return { ok: true, rows }
}

/**
 * Motifs de refus d'une ligne. Mêmes codes que `ImportUsersReport.errors[].reason`
 * côté API : le tableau d'aperçu et le rapport d'import partagent ainsi leurs
 * libellés (`import.error.<CODE>`).
 */
export type ImportIssueReason
  = | 'MISSING_FIELDS'
    | 'INVALID_EMAIL'
    | 'DUPLICATE_IN_FILE'
    | 'AFFILIATION_TOO_LONG'
    | 'EMAIL_ALREADY_USED'

export type ImportIssue = {
  /** Rang de la ligne dans les données (1 = première ligne après l'en-tête). */
  line: number
  email: string
  reason: ImportIssueReason
}

/** Ligne retenue, avec son rang d'origine pour situer les erreurs serveur. */
export type CheckedRow = { line: number, row: ParsedUserRow }

export type ValidationResult = { valid: CheckedRow[], issues: ImportIssue[] }

/**
 * Copie du contrôle de format de `UsersService` (`users.service.ts`). Dupliquée
 * faute de package partagé entre l'API et le front : le serveur reste l'autorité,
 * un écart ici ne fait que déplacer le refus de l'aperçu vers le rapport.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

/**
 * Rejoue localement les contrôles ligne à ligne du serveur (#132), pour annoncer
 * les refus dans l'aperçu plutôt qu'après l'aller-retour d'import.
 *
 * Même ordre de priorité que `UsersService.importUsers` — champs manquants,
 * format, doublon dans le fichier, société ou service trop long, puis adresse déjà prise — pour qu'une ligne
 * reçoive le même motif des deux côtés. `taken` vient de la query de
 * pré-vérification ; sans elle, seuls les trois premiers motifs sont détectables.
 */
export function validateUserRows(
  rows: ParsedUserRow[],
  taken: readonly string[] = [],
): ValidationResult {
  const takenSet = new Set(taken.map(email => email.trim().toLowerCase()))
  const seen = new Set<string>()
  const valid: CheckedRow[] = []
  const issues: ImportIssue[] = []

  rows.forEach((row, index) => {
    const line = index + 1
    // Le serveur normalise l'email avant de le contrôler : sans le même trim +
    // lowercase, deux graphies d'une même adresse passeraient ici le contrôle de
    // doublon pour se faire refuser là-bas.
    const email = row.email.trim().toLowerCase()

    if (!email || !row.firstName.trim() || !row.lastName.trim()) {
      issues.push({ line, email, reason: 'MISSING_FIELDS' })
      return
    }
    if (!EMAIL_RE.test(email)) {
      issues.push({ line, email, reason: 'INVALID_EMAIL' })
      return
    }
    if (seen.has(email)) {
      issues.push({ line, email, reason: 'DUPLICATE_IN_FILE' })
      return
    }
    seen.add(email)

    if (isAffiliationTooLong(row)) {
      issues.push({ line, email, reason: 'AFFILIATION_TOO_LONG' })
      return
    }
    if (takenSet.has(email)) {
      issues.push({ line, email, reason: 'EMAIL_ALREADY_USED' })
      return
    }

    valid.push({ line, row })
  })

  return { valid, issues }
}
